import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, Fragment, useMemo, useCallback, useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Modal, Icon, Input, Advice, Loader, Table, Text, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import Fuse from 'fuse.js';
import Tour from "../Tour";
import { useWeb5Context } from "../../lib/Web5Context";
import NoSearchIcon from "../SvgIcons/NoSearchIcon";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'ModalOptionsTable');
const options = {
  threshold: 0.2,
  keys: ['descripcion']
};
var _ref3 = /*#__PURE__*/React.createElement(Loader, {
  size: "massive"
});
var _ref4 = /*#__PURE__*/React.createElement(Icon, {
  name: "search"
});
const ModalOptionsTable = /*#__PURE__*/memo(_ref => {
  let {
    advice,
    adviceType = 'info',
    className,
    onToggleModal,
    title,
    confirmText,
    cancelText,
    deleteText,
    placeholderSearch,
    isOpen,
    onConfirm,
    onCancel,
    size = 'medium',
    overrides,
    overlayClassName,
    postComponent,
    isLoading,
    inputFocus = true,
    sections,
    columns = [{
      label: 'description',
      id: 'description'
    }, {
      label: 'value',
      id: 'value'
    }, {
      label: 'isRelevant',
      id: 'isRelevant'
    }],
    onChangeFeatured,
    featuredIds,
    featuredFields,
    onClickInput,
    onCloseTour,
    setFirstRowSelector,
    modalSteps,
    showTour = false
  } = _ref;
  const classes = useStyles();
  const theme = useTheme();
  const classNames = classnames(classes.root, className);
  const [search, setSearch] = useState('');
  const firstRowId = useRef(null);
  const [tooltipVisibility, setTooltipVisibility] = useState({});
  const {
    t
  } = useWeb5Context();
  const onSearch = useCallback(value => {
    setSearch(value);
  }, []);
  const handleToggleModal = useCallback(() => {
    setSearch('');
    firstRowId.current = null;
    onToggleModal && onToggleModal();
  }, [onToggleModal]);
  const handleCancel = useCallback(() => {
    onCancel && onCancel();
    handleToggleModal();
  }, [onCancel, handleToggleModal]);
  const handleConfirm = useCallback(() => {
    onConfirm && onConfirm();
    handleToggleModal();
  }, [handleToggleModal, onConfirm]);
  useLayoutEffect(() => {
    if (showTour && isOpen && firstRowId.current) {
      setTimeout(() => {
        document.querySelector(`.${firstRowId.current}`).scrollIntoView({
          block: 'center',
          behavior: 'smooth'
        });
      }, 1000);
    }
  }, [isOpen, showTour]);
  const handleTooltipVisibility = useCallback((id, visible) => {
    setTooltipVisibility(tooltipVisibility => _objectSpread(_objectSpread({}, tooltipVisibility), {}, {
      [id]: visible
    }));
  }, []);
  const sectionFields = useCallback((rows, key) => {
    const finalRows = rows === null || rows === void 0 ? void 0 : rows.reduce((arr, row) => {
      if (row.hiddenByDynamism) return arr;
      if (showTour && isOpen && key !== 'featured' && row.canEditRelevant && !firstRowId.current) {
        firstRowId.current = `description-cell__${row.id}`;
        setFirstRowSelector(`description-cell__${row.id}`);
      }
      const classNamesStarCell = classnames({
        [classes.featured]: featuredIds[row.id],
        [classes.disabled]: !row.canEditRelevant
      }, `featured-cell__${row.id}`);
      const classRow = classnames(classes.row, {
        [classes.clickableRow]: row.canEditRelevant
      });
      const classStar = classnames({
        [classes.clickableStar]: row.canEditRelevant
      });
      const tooltip = featuredIds[row.id] ? t('label_popover_unmark_as_featured') : t('label_popover_mark_as_featured');
      const tooltipId = `${row.id}_${key}`;
      const iconWrapperProps = {
        onMouseEnter: () => handleTooltipVisibility(tooltipId, true),
        onMouseLeave: () => handleTooltipVisibility(tooltipId, false),
        onClick: () => {
          handleTooltipVisibility(tooltipId, false);
          if (!row.canEditRelevant) return;
          onChangeFeatured(row, !featuredIds[row.id]);
        }
      };
      const finalRow = _objectSpread(_objectSpread({}, row), {}, {
        props: {
          className: classRow
        },
        description: /*#__PURE__*/React.createElement(Text, {
          className: `description-cell__${row.id}`,
          color: theme.colors.textLight.secondary
        }, row.description),
        isRelevant: /*#__PURE__*/React.createElement(Tooltip, {
          content: t('label_popover_no_editable_featured_field'),
          visible: !row.canEditRelevant && !!tooltipVisibility[tooltipId]
        }, /*#__PURE__*/React.createElement("div", {
          className: classes.featuredCell
        }, !row.canEditRelevant && /*#__PURE__*/React.createElement("div", _extends({
          className: classes.lock
        }, iconWrapperProps), /*#__PURE__*/React.createElement(Icon, {
          className: classes.disabled,
          name: "lock"
        })), /*#__PURE__*/React.createElement(Tooltip, {
          content: row.canEditRelevant && tooltip,
          visible: row.canEditRelevant && !!tooltipVisibility[tooltipId]
        }, /*#__PURE__*/React.createElement("div", _extends({
          className: classStar
        }, iconWrapperProps), featuredIds[row.id] && /*#__PURE__*/React.createElement(Icon, {
          color: theme.colors.semantic.focus500,
          className: classNamesStarCell,
          name: "star"
        }), !featuredIds[row.id] && /*#__PURE__*/React.createElement(Icon, {
          color: !!tooltipVisibility[tooltipId] ? theme.colors.semantic.focus500 : theme.colors.utility.textSecondary,
          className: classNamesStarCell,
          name: "starBorder"
        })))))
      });
      arr.push(finalRow);
      return arr;
    }, []);
    return /*#__PURE__*/React.createElement(Table, {
      key: key,
      rows: finalRows,
      columns: columns
    });
  }, [columns, showTour, isOpen, classes.featured, classes.disabled, classes.row, classes.clickableRow, classes.clickableStar, classes.featuredCell, classes.lock, featuredIds, t, tooltipVisibility, setFirstRowSelector, handleTooltipVisibility, onChangeFeatured, theme]);
  const baseSchema = useMemo(() => {
    return sections.reduce((arr, section, key) => {
      if (section.isHidden) return arr;
      const newSection = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Text, {
        bold: true
      }, section.description), /*#__PURE__*/React.createElement("div", {
        className: classes.table
      }, sectionFields(section.data, key)));
      arr.push(newSection);
      return arr;
    }, []);
  }, [classes.table, sectionFields, sections]);
  const flatRows = useMemo(() => sections.reduce((arr, section) => {
    arr.push(...section.data);
    return arr;
  }, []), [sections]);
  const finalSections = useCallback(sections => {
    const fuse = new Fuse(flatRows, options);
    if (!sections) return;
    if (search) {
      const matchingOptions = fuse.search(search.toLowerCase()).map(result => result.item);
      if (!(matchingOptions !== null && matchingOptions !== void 0 && matchingOptions.length)) return /*#__PURE__*/React.createElement("div", {
        className: classes === null || classes === void 0 ? void 0 : classes.emptySearchContainer
      }, /*#__PURE__*/React.createElement("div", {
        className: classes === null || classes === void 0 ? void 0 : classes.emptySearchIcon
      }, /*#__PURE__*/React.createElement(NoSearchIcon, {
        color: theme.colors.grey[500]
      })), /*#__PURE__*/React.createElement(Text, {
        type: "subtitle",
        color: theme.colors.grey[500]
      }, t('label_no_results')));
      return sectionFields(matchingOptions);
    } else {
      return baseSchema;
    }
  }, [flatRows, search, classes, theme, sectionFields, baseSchema, t]);
  var _ref2 = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Input, {
    placeholder: placeholderSearch,
    onChange: onSearch,
    value: search,
    preComponent: _ref4,
    autoFocus: inputFocus,
    onClick: onClickInput,
    isFullWidth: true
  }));
  const preComponent = useMemo(() => {
    return /*#__PURE__*/React.createElement(Fragment, null, advice && /*#__PURE__*/React.createElement(Advice, {
      type: adviceType,
      className: classes.advice,
      showIcon: true,
      showCollapse: false
    }, advice), _ref2);
  }, [advice, adviceType, classes.advice, placeholderSearch, onSearch, search, inputFocus, onClickInput]);
  const overridesProps = useMemo(() => _objectSpread({
    content: {
      style: isLoading ? {
        height: '100%'
      } : {
        height: '100%',
        borderRadius: '4px'
      }
    }
  }, overrides), [isLoading, overrides]);
  const getFeaturedFields = useMemo(() => {
    const visibleFeaturedFields = featuredFields.filter(field => !field.hiddenByDynamism);
    const showFeaturedFields = !!visibleFeaturedFields.length && !search;
    return showFeaturedFields ? /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Text, {
      bold: true
    }, t('label_modal_featured_customfields')), /*#__PURE__*/React.createElement("div", {
      className: classes.table
    }, sectionFields(featuredFields, 'featured'))) : null;
  }, [classes.table, featuredFields, search, sectionFields, t]);
  return /*#__PURE__*/React.createElement(Fragment, null, showTour && isOpen && /*#__PURE__*/React.createElement(Tour, {
    onClose: onCloseTour,
    steps: modalSteps,
    delay: 500
  }), /*#__PURE__*/React.createElement(Modal, {
    className: classNames,
    overlayClassName: overlayClassName || classes.overlay,
    title: title,
    isOpen: isOpen,
    size: size,
    useContentStaticHeight: true,
    onRequestClose: handleCancel,
    onCancel: !isLoading && handleCancel,
    onConfirm: !isLoading && handleConfirm,
    cancelText: cancelText,
    confirmText: confirmText,
    deleteText: deleteText,
    preComponent: !isLoading && preComponent,
    postComponent: !isLoading && postComponent,
    overrides: overridesProps
  }, isLoading && /*#__PURE__*/React.createElement("div", {
    className: classes.loader
  }, _ref3), !isLoading && /*#__PURE__*/React.createElement("div", {
    className: classes.section
  }, getFeaturedFields, finalSections(sections))));
});
process.env.NODE_ENV !== "production" ? ModalOptionsTable.propTypes = {
  advice: PropTypes.string,
  adviceType: PropTypes.oneOf(['error', 'info', 'success', 'warning']),
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggleModal: PropTypes.func,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  overrides: PropTypes.object,
  postComponent: PropTypes.node,
  overlayClassName: PropTypes.string,
  isLoading: PropTypes.bool
} : void 0;
export default ModalOptionsTable;